import React, { useState } from 'react';
import { FaTruck, FaEdit, FaSave, FaTimes, FaExclamationTriangle, FaGasPump } from 'react-icons/fa';
import { useModal } from '../components/Modal';
import { useToast } from '../components/Toast';
import '../styles/FuelLevels.css';

const initialTankers = [
  { id: 1, name: 'Tanker A', fuelType: 'JET A-1',      capacity: 1000, level: 720, unit: 'Gal', status: 'available', lastUpdated: '2026-02-20 08:14', updatedBy: 'Carlos Hew' },
  { id: 2, name: 'Tanker B', fuelType: 'JET A-1',      capacity: 1000, level: 450, unit: 'Gal', status: 'in-use',   lastUpdated: '2026-02-20 07:45', updatedBy: 'Mark Lie' },
  { id: 3, name: 'Tanker C', fuelType: 'AVGAS 100LL',  capacity: 800,  level: 144, unit: 'Gal', status: 'available', lastUpdated: '2026-02-19 18:30', updatedBy: 'Sam Jones' },
];

function EditFuelForm({ tanker, onSave, closeModal }) {
  const [value, setValue] = useState(String(tanker.level));
  const [reason, setReason] = useState('');
  const { showToast } = useToast();

  const handleSubmit = (e) => {
    e.preventDefault();
    const num = parseFloat(value);
    if (isNaN(num) || num < 0 || num > tanker.capacity) {
      showToast(`Value must be between 0 and ${tanker.capacity}`, 'error');
      return;
    }
    onSave(tanker.id, num, reason);
    closeModal();
  };

  const pct = Math.round((parseFloat(value) / tanker.capacity) * 100) || 0;
  const barColor = pct < 20 ? '#ef4444' : pct < 40 ? '#f59e0b' : '#22c55e';

  return (
    <form onSubmit={handleSubmit} className="edit-fuel-form">
      <div className="form-row">
        <label className="form-label">Tanker</label>
        <div className="form-static">{tanker.name} — {tanker.fuelType}</div>
      </div>
      <div className="form-row">
        <label className="form-label">Capacity</label>
        <div className="form-static">{tanker.capacity} {tanker.unit}</div>
      </div>
      <div className="form-row">
        <label className="form-label">New level ({tanker.unit})</label>
        <input
          type="number"
          className="form-input"
          value={value}
          onChange={e => setValue(e.target.value)}
          min={0}
          max={tanker.capacity}
          step={0.1}
          required
        />
      </div>
      <div className="fuel-preview">
        <div className="fuel-bar-track">
          <div className="fuel-bar-fill" style={{ width: `${pct}%`, background: barColor }} />
        </div>
        <span style={{ color: barColor }}>{pct}%</span>
      </div>
      <div className="form-row">
        <label className="form-label">Reason / Note</label>
        <input
          type="text"
          className="form-input"
          placeholder="e.g. Manual correction after inspection"
          value={reason}
          onChange={e => setReason(e.target.value)}
        />
      </div>
      <div className="modal-actions">
        <button type="submit" className="btn btn-primary">
          <FaSave /> Save correction
        </button>
        <button type="button" className="btn btn-secondary" onClick={closeModal}>
          <FaTimes /> Cancel
        </button>
      </div>
    </form>
  );
}

export default function FuelLevels() {
  const [tankers, setTankers] = useState(initialTankers);
  const { openModal } = useModal();
  const { success } = useToast();

  const handleSave = (id, newLevel, reason) => {
    setTankers(prev => prev.map(t =>
      t.id === id
        ? { ...t, level: newLevel, lastUpdated: new Date().toLocaleString(), updatedBy: 'Admin' }
        : t
    ));
    success(`Fuel level updated successfully${reason ? ` — "${reason}"` : ''}`);
  };

  const openEdit = (tanker) => {
    openModal(
      `Correct fuel level — ${tanker.name}`,
      <EditFuelForm tanker={tanker} onSave={handleSave} />,
      'sm'
    );
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Fuel Levels</h1>
          <p className="page-subtitle">View and manually correct tanker fuel levels</p>
        </div>
      </div>

      <div className="fl-grid">
        {tankers.map(t => {
          const pct = Math.round((t.level / t.capacity) * 100);
          const barColor = pct < 20 ? '#ef4444' : pct < 40 ? '#f59e0b' : '#22c55e';
          return (
            <div key={t.id} className="fl-card">
              <div className="fl-card-header">
                <div className="fl-title">
                  <FaTruck className="fl-icon" />
                  <span>{t.name}</span>
                  <span className={`tanker-status-badge ${t.status}`}>
                    {t.status === 'in-use' ? 'In Use' : 'Available'}
                  </span>
                </div>
                <button className="btn btn-sm btn-primary" onClick={() => openEdit(t)}>
                  <FaEdit /> Correct
                </button>
              </div>

              <div className="fl-fuel-type">
                <FaGasPump /> {t.fuelType}
              </div>

              <div className="fl-bar-section">
                <div className="fuel-bar-track large">
                  <div className="fuel-bar-fill" style={{ width: `${pct}%`, background: barColor }} />
                </div>
                <div className="fl-bar-labels">
                  <span style={{ color: barColor, fontWeight: 700, fontSize: 22 }}>{pct}%</span>
                  <span className="fl-gal">{t.level} / {t.capacity} {t.unit}</span>
                </div>
              </div>

              {pct < 20 && (
                <div className="fl-warning">
                  <FaExclamationTriangle /> Low fuel — refill required
                </div>
              )}

              <div className="fl-meta">
                <span>Last updated: {t.lastUpdated}</span>
                <span>By: {t.updatedBy}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
